import { useState, useEffect, useCallback, useRef } from "react";
import { Link, Navigate, useParams, useNavigate } from "react-router-dom";
import { db } from "../db";
import * as s from "../../../shared/src/schema";
import { eq } from "drizzle-orm";
import {
  useQuery,
  useCommand,
  useRpc,
  useStore,
  useListVersion,
} from "../store";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import { uuidv7 } from "../../../shared/src/ids";
import { useColors, useDomainColors, useStyles, badge } from "../ui";
import { formatDate, formatDateTime } from "../utils";
import { UserAvatar } from "../avatar";

import type {
  UserInfo,
  UserListItem,
  UnitListItem,
  SessionInfo,
} from "../../../shared/src/protocol";
import { USER_ROLE_VALUES, HOSPITAL_SCOPED_ROLES, UNIT_SCOPED_ROLES, REGION_SCOPED_ROLES } from "../../../shared/src/enums";
import { ConfirmModal } from "../confirm-modal";

function MergeModal({ title, warning, items, sourceId, targetId, onSelectTarget, search, onSearch, onCancel, onConfirm }: {
  title: string; warning: string;
  items: { id: string; name: string | null }[];
  sourceId: string; targetId: string;
  onSelectTarget: (id: string) => void;
  search: string; onSearch: (v: string) => void;
  onCancel: () => void; onConfirm: () => void;
}) {
  const colors = useColors();
  const styles = useStyles();
  const { t } = useI18n();
  const filtered = items
    .filter((item) => item.id !== sourceId)
    .filter((item) => !search.trim() || (item.name || "").toLowerCase().includes(search.toLowerCase()));
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.25)" }}
      onClick={onCancel}>
      <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, boxShadow: "0 8px 24px rgba(0,0,0,0.2)", padding: "1rem 1.25rem", borderRadius: "6px", display: "flex", flexDirection: "column", gap: "0.75rem", minWidth: "20rem", maxWidth: "28rem", width: "90vw" }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ fontWeight: 600, fontSize: "0.95rem", color: colors.text }}>{title}</div>
        <div style={{ fontSize: "0.8rem", color: colors.textMuted }}>{warning}</div>
        <input placeholder={t("app.search")} value={search} onChange={(e) => onSearch(e.target.value)} style={{ ...styles.input }} autoFocus />
        <div style={{ maxHeight: "200px", overflowY: "auto", border: `1px solid ${colors.border}`, borderRadius: "4px" }}>
          {filtered.map((item) => (
            <div key={item.id}
              style={{ padding: "0.4rem 0.6rem", cursor: "pointer", fontSize: "0.875rem", background: targetId === item.id ? colors.primary : "transparent", color: targetId === item.id ? "#fff" : colors.text }}
              onClick={() => onSelectTarget(item.id)}>
              {item.name || item.id.slice(-6)}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <button style={{ ...styles.btn, fontSize: "0.8rem", padding: "0.3rem 0.75rem" }} onClick={onCancel}>{t("common.cancel")}</button>
          <button style={{ ...styles.btnPrimary, fontSize: "0.8rem", padding: "0.3rem 0.75rem", opacity: targetId ? 1 : 0.45, cursor: targetId ? "pointer" : "default" }} disabled={!targetId} onClick={onConfirm}>{t("admin.mergeConfirm")}</button>
        </div>
      </div>
    </div>
  );
}

function useMerge(
  commandType: "hospital.merge" | "unit.merge",
  items: { id: string; name: string | null }[],
  warningKey: string,
  dispatch: (type: "hospital.merge" | "unit.merge", payload: { uuid: string; targetUuid: string }) => Promise<unknown>,
  t: (key: string, vars?: Record<string, string>) => string,
  afterMerge?: (sourceId: string) => void,
) {
  const [source, setSource] = useState<{ id: string; name: string } | null>(null);
  const [target, setTarget] = useState("");
  const [search, setSearch] = useState("");

  const clear = () => { setSource(null); setTarget(""); setSearch(""); };

  const confirm = async () => {
    if (!source || !target) return;
    const sourceId = source.id;
    clear();
    await dispatch(commandType, { uuid: sourceId, targetUuid: target });
    afterMerge?.(sourceId);
  };

  const modal = source ? (
    <MergeModal
      title={t("admin.mergeTitle", { name: source.name })}
      warning={t(warningKey, { name: source.name })}
      items={items}
      sourceId={source.id}
      targetId={target}
      onSelectTarget={setTarget}
      search={search}
      onSearch={setSearch}
      onCancel={clear}
      onConfirm={confirm}
    />
  ) : null;

  return { setSource, modal };
}

function AutocompleteInput({
  placeholder,
  items,
  onSelect,
  onCreate,
  style: inputStyle,
}: {
  placeholder: string;
  items: { id: string; name: string | null }[];
  onSelect: (id: string) => void | Promise<void>;
  onCreate?: (name: string) => Promise<void> | void;
  style?: React.CSSProperties;
}) {
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const selectingRef = useRef(false);
  const { t } = useI18n();
  const colors = useColors();
  const styles = useStyles();

  const q = value.trim().toLowerCase();
  const matches = q
    ? items.filter((i) => (i.name || "").toLowerCase().includes(q))
    : items;
  const exactMatch = q
    ? items.find((i) => (i.name || "").toLowerCase() === q)
    : null;
  const showDropdown = open && (matches.length > 0 || (onCreate && q));

  const handleSelect = async (id: string) => {
    selectingRef.current = true;
    setValue("");
    setOpen(false);
    try {
      await onSelect(id);
    } catch (e) {
      console.error("AutocompleteInput onSelect error:", e);
    }
    selectingRef.current = false;
  };

  const handleCreate = async (name: string) => {
    selectingRef.current = true;
    setValue("");
    setOpen(false);
    try {
      await onCreate?.(name);
    } catch (e) {
      console.error("AutocompleteInput onCreate error:", e);
    }
    selectingRef.current = false;
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() =>
          setTimeout(() => {
            if (!selectingRef.current) setOpen(false);
          }, 150)
        }
        style={{ ...styles.input, ...inputStyle }}
      />
      {showDropdown && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 100,
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            minWidth: "160px",
            maxHeight: "200px",
            overflow: "auto",
          }}
        >
          {matches.map((item) => (
            <div
              key={item.id}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(item.id);
              }}
              style={{
                padding: "4px 8px",
                cursor: "pointer",
                color: colors.text,
                borderBottom: `1px solid ${colors.border}`,
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background = colors.bgAlt)
              }
              onMouseOut={(e) => (e.currentTarget.style.background = "")}
            >
              {item.name || item.id}
            </div>
          ))}
          {onCreate && !exactMatch && q && (
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                handleCreate(value.trim());
              }}
              style={{
                padding: "4px 8px",
                cursor: "pointer",
                color: colors.textMuted,
                fontStyle: "italic",
                borderBottom: `1px solid ${colors.border}`,
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background = colors.bgAlt)
              }
              onMouseOut={(e) => (e.currentTarget.style.background = "")}
            >
              {t("admin.createNew")}: "{q}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function UsersPage() {
  const { t } = useI18n();
  const dispatch = useCommand();
  const rpc = useRpc();
  const { online } = useStore();
  const [email, setEmail] = useState("");
  const [newRole, setNewRole] = useState<string>("medicRole1");
  const [newHospitals, setNewHospitals] = useState<string[]>([]);
  const [newUnits, setNewUnits] = useState<string[]>([]);
  const [newRegions, setNewRegions] = useState<string[]>([]);
  const [sendInvite, setSendInvite] = useState(true);
  const [userList, setUserList] = useState<UserListItem[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const [deleteModal, setDeleteModal] = useState<{ kind: "blocked"; message: string } | { kind: "confirm"; id: string; name: string } | null>(null);
  const colors = useColors();
  const styles = useStyles();

  const { data: hospitalList } = useQuery(() =>
    db.select().from(s.hospitals).where(eq(s.hospitals.archived, 0)),
  );
  const { data: unitList } = useQuery(() =>
    db.select().from(s.units).where(eq(s.units.archived, 0)),
  );
  const { data: regionList } = useQuery(() =>
    db.select().from(s.regions).where(eq(s.regions.archived, 0)),
  );

  const hospitalMap = new Map(
    (hospitalList || []).map((h) => [h.id, h.name || h.id]),
  );
  const unitMap = new Map((unitList || []).map((u) => [u.id, u.name || u.id]));
  const regionMap = new Map((regionList || []).map((r) => [r.id, r.name || r.id]));

  const listVersion = useListVersion();

  const refresh = useCallback(() => {
    if (!online) return;
    rpc<{ users: UserListItem[] }>({ type: "listUsers" })
      .then((data) => setUserList(data.users || []))
      .catch(() => {});
  }, [rpc, online]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Re-fetch when server broadcasts a list invalidation
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    if (listVersion > 0) refreshRef.current();
  }, [listVersion]);

  const addUser = async () => {
    if (!email.trim()) return;
    const trimmed = email.trim().toLowerCase();
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      alert(t("admin.invalidEmail"));
      return;
    }
    // Prevent duplicate: check if a user with this email already exists
    if (userList.some((u) => u.email?.toLowerCase() === trimmed)) {
      alert(t("admin.duplicateEmail", { email: trimmed }));
      return;
    }
    const uuid = uuidv7();
    await dispatch("user.create", { uuid, email: trimmed });
    if (newRole !== "unauthorized") {
      await dispatch("user.setRole", { uuid, role: newRole });
    }
    const hospitals: string[] = [...newHospitals];
    const units: string[] = [...newUnits];
    const regions: string[] = [...newRegions];
    for (const hId of newHospitals) {
      await dispatch("user.attachHospital", { uuid, hospitalUuid: hId });
    }
    for (const uId of newUnits) {
      await dispatch("user.attachUnit", { uuid, unitUuid: uId });
    }
    for (const rId of newRegions) {
      await dispatch("user.attachRegion", { uuid, regionUuid: rId });
    }
    if (sendInvite) {
      await dispatch("user.sendInvite", { uuid });
    }
    setEmail("");
    setNewHospitals([]);
    setNewUnits([]);
    setNewRegions([]);
    setUserList((prev) => [
      ...prev,
      {
        id: uuid,
        email: trimmed,
        name: null,
        picture: null,
        role: newRole,
        hospitals,
        units,
        regions,
        firstLoginAt: null,
        lastLoginAt: null,
        inviteSentAt: sendInvite ? Date.now() : null,
      },
    ]);
  };

  const requestDelete = async (userId: string, name: string) => {
    try {
      const result = await rpc<{ deletable: boolean; reason?: string }>({ type: "canDelete", entityType: "user", entityId: userId });
      if (result.deletable) {
        setDeleteModal({ kind: "confirm", id: userId, name });
      } else {
        setDeleteModal({ kind: "blocked", message: t("common.deleteBlocked", { reason: result.reason || "" }) });
      }
    } catch { /* ignore */ }
  };

  const confirmDelete = async () => {
    if (deleteModal?.kind !== "confirm") return;
    const { id } = deleteModal;
    setDeleteModal(null);
    try {
      await rpc({ type: "deleteEntity", entityType: "user", entityId: id });
      setUserList((prev: UserListItem[]) => prev.filter((u: UserListItem) => u.id !== id));
    } catch (e) {
      setDeleteModal({ kind: "blocked", message: (e as any)?.message || "Delete failed" });
    }
  };

  const act = async (type: string, payload: Record<string, unknown>) => {
    // Optimistically update local list state BEFORE dispatching (dispatch awaits OPFS writes)
    const uuid = (payload as { uuid: string }).uuid;
    setUserList((prev) =>
      prev.map((u) => {
        if (u.id !== uuid) return u;
        switch (type) {
          case "user.setEmail":
            return { ...u, email: (payload as any).email };
          case "user.sendInvite":
            return { ...u, inviteSentAt: Date.now() };
          case "user.setRole":
            return { ...u, role: (payload as any).role };
          case "user.attachHospital":
            return {
              ...u,
              hospitals: [...u.hospitals, (payload as any).hospitalUuid],
            };
          case "user.detachHospital":
            return {
              ...u,
              hospitals: u.hospitals.filter(
                (h: string) => h !== (payload as any).hospitalUuid,
              ),
            };
          case "user.attachUnit":
            return { ...u, units: [...u.units, (payload as any).unitUuid] };
          case "user.detachUnit":
            return {
              ...u,
              units: u.units.filter(
                (x: string) => x !== (payload as any).unitUuid,
              ),
            };
          case "user.attachRegion":
            return { ...u, regions: [...(u.regions || []), (payload as any).regionUuid] };
          case "user.detachRegion":
            return {
              ...u,
              regions: (u.regions || []).filter(
                (x: string) => x !== (payload as any).regionUuid,
              ),
            };
          default:
            return u;
        }
      }),
    );
    await dispatch(type as any, payload as any);
  };


  const tagStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "1px 6px",
    background: colors.bgAlt,
    border: `1px solid ${colors.border}`,
    whiteSpace: "nowrap",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Toolbar: form left, search right */}
      <div
        style={{
          display: "flex",
          alignItems: "start",
          gap: "8px",
          padding: "8px 12px",
          flexShrink: 0,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            addUser();
          }}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* Row 1: email + add + invite */}
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <input
              placeholder={t("admin.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ ...styles.input, width: "220px" }}
            />
            <button type="submit" style={styles.btnPrimary}>
              {t("admin.addUser")}
            </button>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                color: colors.textMuted,
                whiteSpace: "nowrap",
              }}
            >
              <input
                type="checkbox"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
              />
              {t("admin.sendInviteEmail")}
            </label>
          </div>
          {/* Row 2: role + scope */}
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <select
              value={newRole}
              onChange={(e) => {
                setNewRole(e.target.value);
                setNewHospitals([]);
                setNewUnits([]);
                setNewRegions([]);
              }}
              style={{ ...styles.select, width: "auto", padding: "4px 6px" }}
            >
              {USER_ROLE_VALUES.map((r) => (
                <option key={r} value={r}>
                  {t(`role.${r}`)}
                </option>
              ))}
            </select>
            {HOSPITAL_SCOPED_ROLES.includes(newRole) && (
              <>
                {newHospitals.map((hId) => (
                  <span key={hId} style={tagStyle}>
                    {hospitalMap.get(hId) || hId}
                    <span
                      style={{
                        cursor: "pointer",
                        color: colors.danger,
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                      onClick={() =>
                        setNewHospitals((prev) => prev.filter((x) => x !== hId))
                      }
                    >
                      ×
                    </span>
                  </span>
                ))}
                <AutocompleteInput
                  placeholder={t("user.addHospital")}
                  items={(hospitalList || []).filter(
                    (h) => !newHospitals.includes(h.id),
                  )}
                  onSelect={(id) => setNewHospitals((prev) => [...prev, id])}
                  onCreate={async (name) => {
                    const uuid = uuidv7();
                    await dispatch("hospital.create", { uuid });
                    await dispatch("hospital.setName", { uuid, name });
                    setNewHospitals((prev) => [...prev, uuid]);
                  }}
                  style={{ padding: "4px 6px", width: "160px" }}
                />
              </>
            )}
            {UNIT_SCOPED_ROLES.includes(newRole) && (
              <>
                {newUnits.map((uId) => (
                  <span key={uId} style={tagStyle}>
                    {unitMap.get(uId) || uId}
                    <span
                      style={{
                        cursor: "pointer",
                        color: colors.danger,
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                      onClick={() =>
                        setNewUnits((prev) => prev.filter((x) => x !== uId))
                      }
                    >
                      ×
                    </span>
                  </span>
                ))}
                <AutocompleteInput
                  placeholder={t("user.addUnit")}
                  items={(unitList || []).filter(
                    (u) => !newUnits.includes(u.id),
                  )}
                  onSelect={(id) => setNewUnits((prev) => [...prev, id])}
                  onCreate={async (name) => {
                    const uuid = uuidv7();
                    await dispatch("unit.create", { uuid });
                    await dispatch("unit.setName", { uuid, name });
                    setNewUnits((prev) => [...prev, uuid]);
                  }}
                  style={{ padding: "4px 6px", width: "160px" }}
                />
              </>
            )}
            {REGION_SCOPED_ROLES.includes(newRole as never) && (
              <>
                {newRegions.map((rId) => (
                  <span key={rId} style={tagStyle}>
                    {regionMap.get(rId) || rId}
                    <span
                      style={{
                        cursor: "pointer",
                        color: colors.danger,
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                      onClick={() =>
                        setNewRegions((prev) => prev.filter((x) => x !== rId))
                      }
                    >
                      ×
                    </span>
                  </span>
                ))}
                <AutocompleteInput
                  placeholder={t("user.addRegion")}
                  items={(regionList || []).filter(
                    (r) => !newRegions.includes(r.id),
                  )}
                  onSelect={(id) => setNewRegions((prev) => [...prev, id])}
                  style={{ padding: "4px 6px", width: "160px" }}
                />
              </>
            )}
          </div>
        </form>
        <input
          placeholder={t("app.search")}
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          style={{ ...styles.input, width: "180px", flexShrink: 0 }}
        />
      </div>
      <div style={{ flex: 1, overflow: "auto", minWidth: 0 }}>
        <table style={{ ...styles.table, minWidth: "max-content" }}>
          <thead>
            <tr>
              <th style={styles.th}>{t("admin.email")}</th>
              <th style={styles.th}>{t("admin.name")}</th>
              <th style={styles.th}>{t("admin.activity")}</th>
              <th style={styles.th}>{t("admin.role")}</th>
              <th style={styles.th}>{t("user.permissions")}</th>
            </tr>
          </thead>
          <tbody>
            {userList
              .filter(
                (u) =>
                  !userSearch.trim() ||
                  `${u.email} ${u.name || ""}`
                    .toLowerCase()
                    .includes(userSearch.toLowerCase()),
              )
              .map((u) => {
                const uHosps = u.hospitals;
                const uUnits = u.units;
                const uRegions = u.regions || [];
                const availHosps = (hospitalList || []).filter(
                  (h) => !uHosps.includes(h.id),
                );
                const availUnits = (unitList || []).filter(
                  (x) => !uUnits.includes(x.id),
                );
                const availRegions = (regionList || []).filter(
                  (r) => !uRegions.includes(r.id),
                );
                const hasLoggedIn = !!u.firstLoginAt;

                return (
                  <tr key={u.id} style={{ verticalAlign: "top" }}>
                    <td style={styles.td}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <Link to={`/users/${u.id}`}>
                          <UserAvatar
                            userId={u.id}
                            picture={u.picture}
                            name={u.name || u.email}
                            size={22}
                          />
                        </Link>
                        <input
                          defaultValue={u.email}
                          key={u.email}
                          onBlur={(e) => {
                            const v = e.target.value.trim().toLowerCase();
                            if (v && v !== u.email)
                              act("user.setEmail", { uuid: u.id, email: v });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter")
                              (e.target as HTMLInputElement).blur();
                          }}
                          style={{
                            ...styles.input,
                            padding: "2px 4px",
                            width: "180px",
                            color: colors.primary,
                          }}
                        />
                        <button
                          style={{ ...styles.btn, fontSize: "0.7rem", color: colors.danger, padding: "1px 6px", minHeight: "auto", marginLeft: "auto" }}
                          onClick={() => requestDelete(u.id, u.email || u.id.slice(-6))}
                        >
                          {t("common.delete")}
                        </button>
                      </div>
                    </td>
                    <td style={styles.td}>{u.name || ""}</td>
                    {/* Activity: login times + invite */}
                    <td style={styles.td}>
                      <div style={{ lineHeight: "1.4" }}>
                        {hasLoggedIn ? (
                          <div style={{ color: colors.text }}>
                            {formatDate(u.firstLoginAt)} —{" "}
                            {formatDate(u.lastLoginAt)}
                          </div>
                        ) : u.inviteSentAt ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <button
                              style={{
                                ...styles.btn,
                                padding: "1px 6px",
                                minHeight: "auto",
                              }}
                              onClick={() =>
                                act("user.sendInvite", { uuid: u.id })
                              }
                            >
                              {t("admin.resendInvite")}
                            </button>
                            <span style={{ color: colors.textMuted }}>
                              {formatDate(u.inviteSentAt)}
                            </span>
                          </div>
                        ) : (
                          <button
                            style={{
                              ...styles.btn,
                              padding: "1px 6px",
                              minHeight: "auto",
                            }}
                            onClick={() =>
                              act("user.sendInvite", { uuid: u.id })
                            }
                          >
                            {t("admin.sendInvite")}
                          </button>
                        )}
                      </div>
                    </td>
                    {/* Role dropdown */}
                    <td style={styles.td}>
                      <select
                        value={u.role}
                        onChange={(e) =>
                          act("user.setRole", {
                            uuid: u.id,
                            role: e.target.value,
                          })
                        }
                        style={{ ...styles.select, padding: "2px 4px" }}
                      >
                        {USER_ROLE_VALUES.map((r) => (
                          <option key={r} value={r}>
                            {t(`role.${r}`)}
                          </option>
                        ))}
                      </select>
                    </td>
                    {/* Scope: Hospitals or Units depending on role */}
                    <td style={styles.td}>
                      {HOSPITAL_SCOPED_ROLES.includes(u.role) && (
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          {uHosps.map((hId) => (
                            <span key={hId} style={tagStyle}>
                              {hospitalMap.get(hId) || hId}
                              <span
                                style={{
                                  cursor: "pointer",
                                  color: colors.danger,
                                  fontWeight: 700,
                                  lineHeight: 1,
                                }}
                                onClick={() =>
                                  act("user.detachHospital", {
                                    uuid: u.id,
                                    hospitalUuid: hId,
                                  })
                                }
                              >
                                ×
                              </span>
                            </span>
                          ))}
                          <AutocompleteInput
                            key={`hosp-${u.id}-${uHosps.length}`}
                            placeholder={t("user.addHospital")}
                            items={availHosps}
                            onSelect={(id) =>
                              act("user.attachHospital", {
                                uuid: u.id,
                                hospitalUuid: id,
                              })
                            }
                            onCreate={async (name) => {
                              const uuid = uuidv7();
                              await dispatch("hospital.create", { uuid });
                              await dispatch("hospital.setName", {
                                uuid,
                                name,
                              });
                              await act("user.attachHospital", {
                                uuid: u.id,
                                hospitalUuid: uuid,
                              });
                            }}
                            style={{ padding: "2px 6px", width: "140px" }}
                          />
                        </div>
                      )}
                      {UNIT_SCOPED_ROLES.includes(u.role) && (
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          {uUnits.map((uId) => (
                            <span key={uId} style={tagStyle}>
                              {unitMap.get(uId) || uId}
                              <span
                                style={{
                                  cursor: "pointer",
                                  color: colors.danger,
                                  fontWeight: 700,
                                  lineHeight: 1,
                                }}
                                onClick={() =>
                                  act("user.detachUnit", {
                                    uuid: u.id,
                                    unitUuid: uId,
                                  })
                                }
                              >
                                ×
                              </span>
                            </span>
                          ))}
                          <AutocompleteInput
                            key={`unit-${u.id}-${uUnits.length}`}
                            placeholder={t("user.addUnit")}
                            items={availUnits}
                            onSelect={(id) =>
                              act("user.attachUnit", {
                                uuid: u.id,
                                unitUuid: id,
                              })
                            }
                            onCreate={async (name) => {
                              const uuid = uuidv7();
                              await dispatch("unit.create", { uuid });
                              await dispatch("unit.setName", { uuid, name });
                              await act("user.attachUnit", {
                                uuid: u.id,
                                unitUuid: uuid,
                              });
                            }}
                            style={{ padding: "2px 6px", width: "140px" }}
                          />
                        </div>
                      )}
                      {REGION_SCOPED_ROLES.includes(u.role as never) && (
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          {uRegions.map((rId) => (
                            <span key={rId} style={tagStyle}>
                              {regionMap.get(rId) || rId}
                              <span
                                style={{
                                  cursor: "pointer",
                                  color: colors.danger,
                                  fontWeight: 700,
                                  lineHeight: 1,
                                }}
                                onClick={() =>
                                  act("user.detachRegion", {
                                    uuid: u.id,
                                    regionUuid: rId,
                                  })
                                }
                              >
                                ×
                              </span>
                            </span>
                          ))}
                          <span key={`region-input-${u.id}`}>
                            <AutocompleteInput
                              placeholder={t("user.addRegion")}
                              items={availRegions}
                              onSelect={(id) =>
                                act("user.attachRegion", {
                                  uuid: u.id,
                                  regionUuid: id,
                                })
                              }
                              style={{ padding: "2px 6px", width: "140px" }}
                            />
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {deleteModal?.kind === "blocked" && <ConfirmModal message={deleteModal.message} onCancel={() => setDeleteModal(null)} />}
      {deleteModal?.kind === "confirm" && <ConfirmModal message={t("common.deleteConfirm", { name: deleteModal.name })} onCancel={() => setDeleteModal(null)} onConfirm={confirmDelete} />}
    </div>
  );
}

interface UserEvent {
  id: string;
  type: string;
  entityId: string;
  payload: Record<string, unknown>;
  serverTimestamp: number;
}

export function UserPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useI18n();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const colors = useColors();
  const styles = useStyles();
  const { rpc, online } = useStore();
  const dispatch = useCommand();

  const [user, setUser] = useState<UserInfo | null>(null);
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [events, setEvents] = useState<UserEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [hasMoreEvents, setHasMoreEvents] = useState(false);
  const [eventCount, setEventCount] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Local DB lists for hospitals/units/regions
  const { data: hospitalList } = useQuery(() =>
    db.select().from(s.hospitals).where(eq(s.hospitals.archived, 0)),
  );
  const { data: unitList } = useQuery(() =>
    db.select().from(s.units).where(eq(s.units.archived, 0)),
  );
  const { data: regionList } = useQuery(() =>
    db.select().from(s.regions).where(eq(s.regions.archived, 0)),
  );

  const isAdmin = currentUser?.role === "admin";

  // Check access - users can only view themselves unless admin
  useEffect(() => {
    if (!currentUser) return;
    if (currentUser.role !== "admin" && currentUser.userId !== id) {
      navigate("/", { replace: true });
    }
  }, [currentUser, id, navigate]);

  // Fetch user and sessions via WS RPC (wait for connection)
  useEffect(() => {
    if (!id || !online) return;
    setLoading(true);

    Promise.all([
      rpc<UserInfo>({ type: "user", userId: id }).catch(() => null),
      rpc<{ sessions: SessionInfo[] }>({
        type: "userSessions",
        userId: id,
      }).catch(() => ({ sessions: [] })),
    ])
      .then(([userData, sessionsData]) => {
        setUser(userData);
        setSessions(sessionsData.sessions || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [rpc, id, online]);

  // Fetch events via WS RPC
  const fetchEvents = useCallback(
    async (beforeTs?: number) => {
      if (!id || !online) return;
      setEventsLoading(true);
      try {
        const data = await rpc<{
          events: UserEvent[];
          hasMore: boolean;
          totalCount: number;
        }>({
          type: "userEvents",
          userId: id,
          beforeTs,
          limit: 50,
        });

        if (beforeTs) {
          setEvents((prev) => [...prev, ...data.events]);
        } else {
          setEvents(data.events);
        }
        setHasMoreEvents(data.hasMore);
        setEventCount(data.totalCount);
      } catch {
        // ignore
      } finally {
        setEventsLoading(false);
      }
    },
    [rpc, id, online],
  );

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const terminateSession = async (targetSessionId: string) => {
    if (!id) return;
    try {
      await rpc({ type: "terminateSession", userId: id, targetSessionId });
      setSessions((prev) => prev.filter((s) => s.id !== targetSessionId));
    } catch {
      // ignore
    }
  };

  const toggleExpand = (eventId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const formatTime = (ts: number) => formatDateTime(ts);

  if (loading)
    return (
      <div style={{ padding: "12px", color: colors.textMuted }}>
        {t("app.loading")}
      </div>
    );
  if (!user)
    return (
      <div style={{ padding: "12px" }}>
        <p style={{ color: colors.textMuted }}>{t("user.notFound")}</p>
      </div>
    );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div style={{ ...styles.toolbar, flexShrink: 0 }}>
        <UserAvatar
          userId={user.id}
          picture={user.picture}
          name={user.name || user.email}
          size={28}
        />
        {isAdmin ? (
          <input
            defaultValue={user.email}
            key={user.email}
            onBlur={(e) => {
              const v = e.target.value.trim().toLowerCase();
              if (v && v !== user.email) {
                dispatch("user.setEmail", { uuid: id!, email: v });
                setUser((prev) => (prev ? { ...prev, email: v } : prev));
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            style={{
              ...styles.input,
              fontWeight: 600,
              width: "260px",
              padding: "2px 6px",
            }}
          />
        ) : (
          <span style={{ fontWeight: 600 }}>{user.email}</span>
        )}
        {user.name && (
          <span style={{ color: colors.textMuted }}>({user.name})</span>
        )}
        {user?.role ? (
          <span style={badge(colors.primary)}>{t(`role.${user.role}`)}</span>
        ) : null}
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflow: "auto", minWidth: 0 }}>
        {/* Permissions (admin only) */}
        {isAdmin && (
          <div
            style={{
              padding: "12px",
              borderBottom: `1px solid ${colors.border}`,
            }}
          >
            <h3 style={{ margin: "0 0 8px", color: colors.text }}>
              {t("user.permissions")}
            </h3>

            {/* Role dropdown */}
            <div style={{ marginBottom: "12px" }}>
              <div
                style={{
                  fontWeight: 600,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                  marginBottom: "4px",
                }}
              >
                {t("admin.role")}
              </div>
              <select
                value={user.role}
                onChange={(e) => {
                  dispatch("user.setRole", { uuid: id!, role: e.target.value });
                  setUser((prev) =>
                    prev ? { ...prev, role: e.target.value } : prev,
                  );
                }}
                style={{ ...styles.select, maxWidth: "250px" }}
              >
                {USER_ROLE_VALUES.map((r) => (
                  <option key={r} value={r}>
                    {t(`role.${r}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Hospitals (hospital-scoped roles) */}
            {HOSPITAL_SCOPED_ROLES.includes(user.role) && (
              <div style={{ marginBottom: "12px" }}>
                <div
                  style={{
                    fontWeight: 600,
                    color: colors.textMuted,
                    textTransform: "uppercase",
                    marginBottom: "4px",
                  }}
                >
                  {t("user.hospitals")}
                </div>
                {user.hospitals.length === 0 && (
                  <div style={{ color: colors.textMuted, marginBottom: "4px" }}>
                    {t("user.noHospitals")}
                  </div>
                )}
                {user.hospitals.map((hId) => {
                  const h = (hospitalList || []).find((x) => x.id === hId);
                  return (
                    <div
                      key={hId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginBottom: "2px",
                      }}
                    >
                      <span style={{ color: colors.text }}>
                        {h?.name || hId}
                      </span>
                      <button
                        style={{
                          ...styles.btn,
                          padding: "1px 6px",
                          color: colors.danger,
                          minHeight: "auto",
                        }}
                        onClick={() => {
                          dispatch("user.detachHospital", {
                            uuid: id!,
                            hospitalUuid: hId,
                          });
                          setUser((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  hospitals: prev.hospitals.filter(
                                    (x) => x !== hId,
                                  ),
                                }
                              : prev,
                          );
                        }}
                      >
                        {t("user.remove")}
                      </button>
                    </div>
                  );
                })}
                {(() => {
                  const available = (hospitalList || []).filter(
                    (h) => !user.hospitals.includes(h.id),
                  );
                  if (available.length === 0) return null;
                  return (
                    <select
                      style={{
                        ...styles.select,
                        maxWidth: "250px",
                        marginTop: "4px",
                      }}
                      value=""
                      onChange={(e) => {
                        if (!e.target.value) return;
                        dispatch("user.attachHospital", {
                          uuid: id!,
                          hospitalUuid: e.target.value,
                        });
                        setUser((prev) =>
                          prev
                            ? {
                                ...prev,
                                hospitals: [...prev.hospitals, e.target.value],
                              }
                            : prev,
                        );
                      }}
                    >
                      <option value="">{t("user.addHospital")}</option>
                      {available.map((h) => (
                        <option key={h.id} value={h.id}>
                          {h.name || h.id}
                        </option>
                      ))}
                    </select>
                  );
                })()}
              </div>
            )}

            {/* Units (unit-scoped roles) */}
            {UNIT_SCOPED_ROLES.includes(user.role) && (
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    color: colors.textMuted,
                    textTransform: "uppercase",
                    marginBottom: "4px",
                  }}
                >
                  {t("user.units")}
                </div>
                {user.units.length === 0 && (
                  <div style={{ color: colors.textMuted, marginBottom: "4px" }}>
                    {t("user.noUnits")}
                  </div>
                )}
                {user.units.map((uId) => {
                  const u = (unitList || []).find((x) => x.id === uId);
                  return (
                    <div
                      key={uId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginBottom: "2px",
                      }}
                    >
                      <span style={{ color: colors.text }}>
                        {u?.name || uId}
                      </span>
                      <button
                        style={{
                          ...styles.btn,
                          padding: "1px 6px",
                          color: colors.danger,
                          minHeight: "auto",
                        }}
                        onClick={() => {
                          dispatch("user.detachUnit", {
                            uuid: id!,
                            unitUuid: uId,
                          });
                          setUser((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  units: prev.units.filter((x) => x !== uId),
                                }
                              : prev,
                          );
                        }}
                      >
                        {t("user.remove")}
                      </button>
                    </div>
                  );
                })}
                {(() => {
                  const available = (unitList || []).filter(
                    (u) => !user.units.includes(u.id),
                  );
                  if (available.length === 0) return null;
                  return (
                    <select
                      style={{
                        ...styles.select,
                        maxWidth: "250px",
                        marginTop: "4px",
                      }}
                      value=""
                      onChange={(e) => {
                        if (!e.target.value) return;
                        dispatch("user.attachUnit", {
                          uuid: id!,
                          unitUuid: e.target.value,
                        });
                        setUser((prev) =>
                          prev
                            ? {
                                ...prev,
                                units: [...prev.units, e.target.value],
                              }
                            : prev,
                        );
                      }}
                    >
                      <option value="">{t("user.addUnit")}</option>
                      {available.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name || u.id}
                        </option>
                      ))}
                    </select>
                  );
                })()}
              </div>
            )}

            {/* Regions (region-scoped roles: OMO, SMO) */}
            {REGION_SCOPED_ROLES.includes(user.role as never) && (
              <div style={{ marginTop: "12px" }}>
                <div
                  style={{
                    fontWeight: 600,
                    color: colors.textMuted,
                    textTransform: "uppercase",
                    marginBottom: "4px",
                  }}
                >
                  {t("user.regions")}
                </div>
                <div style={{ color: colors.textMuted, fontSize: "0.85em", marginBottom: "4px" }}>
                  {t("user.noRegionGlobal")}
                </div>
                {(user.regions || []).map((rId) => {
                  const r = (regionList || []).find((x) => x.id === rId);
                  return (
                    <div
                      key={rId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginBottom: "2px",
                      }}
                    >
                      <span style={{ color: colors.text }}>
                        {r?.name || rId}
                      </span>
                      <button
                        style={{
                          ...styles.btn,
                          padding: "1px 6px",
                          color: colors.danger,
                          minHeight: "auto",
                        }}
                        onClick={() => {
                          dispatch("user.detachRegion", {
                            uuid: id!,
                            regionUuid: rId,
                          });
                          setUser((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  regions: (prev.regions || []).filter((x) => x !== rId),
                                }
                              : prev,
                          );
                        }}
                      >
                        {t("user.remove")}
                      </button>
                    </div>
                  );
                })}
                {(() => {
                  const available = (regionList || []).filter(
                    (r) => !(user.regions || []).includes(r.id),
                  );
                  if (available.length === 0) return null;
                  return (
                    <select
                      style={{
                        ...styles.select,
                        maxWidth: "250px",
                        marginTop: "4px",
                      }}
                      value=""
                      onChange={(e) => {
                        if (!e.target.value) return;
                        dispatch("user.attachRegion", {
                          uuid: id!,
                          regionUuid: e.target.value,
                        });
                        setUser((prev) =>
                          prev
                            ? {
                                ...prev,
                                regions: [...(prev.regions || []), e.target.value],
                              }
                            : prev,
                        );
                      }}
                    >
                      <option value="">{t("user.addRegion")}</option>
                      {available.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name || r.id}
                        </option>
                      ))}
                    </select>
                  );
                })()}
              </div>
            )}

            {/* Broadcast permissions — independent of role scope */}
            <div style={{ marginTop: "12px" }}>
              <div
                style={{
                  fontWeight: 600,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                  marginBottom: "4px",
                }}
              >
                {t("user.broadcastPermissions")}
              </div>
              {HOSPITAL_SCOPED_ROLES.includes(user.role as never) && (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    color: colors.text,
                    marginBottom: "4px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!user.canBroadcastToOwnHospital}
                    onChange={(e) => {
                      const value = e.target.checked;
                      dispatch("user.setCanBroadcastToOwnHospital", {
                        uuid: id!,
                        value,
                      });
                      setUser((prev) =>
                        prev ? { ...prev, canBroadcastToOwnHospital: value } : prev,
                      );
                    }}
                  />
                  {t("user.canBroadcastHospital")}
                </label>
              )}
              {UNIT_SCOPED_ROLES.includes(user.role as never) && (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    color: colors.text,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!user.canBroadcastToOwnUnit}
                    onChange={(e) => {
                      const value = e.target.checked;
                      dispatch("user.setCanBroadcastToOwnUnit", {
                        uuid: id!,
                        value,
                      });
                      setUser((prev) =>
                        prev ? { ...prev, canBroadcastToOwnUnit: value } : prev,
                      );
                    }}
                  />
                  {t("user.canBroadcastUnit")}
                </label>
              )}
              {!HOSPITAL_SCOPED_ROLES.includes(user.role as never) &&
                !UNIT_SCOPED_ROLES.includes(user.role as never) && (
                  <div style={{ color: colors.textMuted, fontSize: "0.85em" }}>
                    {t("user.broadcastNotApplicable")}
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Sessions */}
        <div style={{ padding: "12px" }}>
          <h3 style={{ margin: "0 0 8px", color: colors.text }}>
            {t("user.sessions")}
          </h3>
          {sessions.length === 0 ? (
            <p style={{ color: colors.textMuted }}>{t("user.noSessions")}</p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>{t("user.sessionId")}</th>
                  <th style={styles.th}>{t("user.lastActive")}</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id}>
                    <td
                      style={{
                        ...styles.td,
                        fontFamily: "ui-monospace, monospace",
                      }}
                    >
                      <span
                        style={{
                          maxWidth: "180px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "inline-block",
                          verticalAlign: "middle",
                        }}
                        title={s.id}
                      >
                        {s.id}
                      </span>
                      {s.isCurrent && (
                        <span
                          style={{
                            ...badge(colors.success),
                            marginLeft: "8px",
                          }}
                        >
                          {t("user.current")}
                        </span>
                      )}
                    </td>
                    <td style={{ ...styles.td }}>{formatTime(s.lastSeenAt)}</td>
                    <td style={styles.td}>
                      <button
                        style={{ ...styles.btn, color: colors.danger }}
                        onClick={() => terminateSession(s.id)}
                        disabled={s.isCurrent}
                      >
                        {t("user.terminate")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Events */}
        <div
          style={{ padding: "12px", borderTop: `1px solid ${colors.border}` }}
        >
          <h3 style={{ margin: "0 0 8px", color: colors.text }}>
            {t("user.actions")}{" "}
            <span style={{ fontWeight: 400, color: colors.textMuted }}>
              ({eventCount})
            </span>
          </h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>{t("audit.time")}</th>
                <th style={styles.th}>{t("audit.type")}</th>
                <th style={styles.th}>{t("audit.payload")}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id}>
                  <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                    {formatTime(ev.serverTimestamp)}
                  </td>
                  <td style={styles.td}>
                    <span style={badge(colors.primary)}>{ev.type}</span>
                  </td>
                  <td style={styles.td}>
                    <button
                      style={{ ...styles.btn, padding: "2px 6px" }}
                      onClick={() => toggleExpand(ev.id)}
                    >
                      {expanded.has(ev.id) ? "−" : "+"}
                    </button>
                    {expanded.has(ev.id) && (
                      <pre
                        style={{
                          marginTop: "4px",
                          padding: "4px",
                          background: colors.bgAlt,
                          overflow: "auto",
                          maxWidth: "400px",
                          border: `1px solid ${colors.border}`,
                          borderRadius: "4px",
                          color: colors.text,
                        }}
                      >
                        {JSON.stringify(ev.payload, null, 2)}
                      </pre>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {eventsLoading && (
            <div
              style={{
                padding: "12px",
                textAlign: "center",
                color: colors.textMuted,
              }}
            >
              {t("app.loading")}
            </div>
          )}

          {hasMoreEvents && !eventsLoading && (
            <div style={{ padding: "12px", textAlign: "center" }}>
              <button
                onClick={() =>
                  events.length > 0 &&
                  fetchEvents(events[events.length - 1].serverTimestamp)
                }
                style={styles.btn}
              >
                {t("audit.loadMore")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function RegionsPage() {
  return <Navigate to="/hospitals" replace />;
}

function PopoverAdd({
  label,
  placeholder,
  onAdd,
  btnStyle,
}: {
  label: string;
  placeholder: string;
  onAdd: (name: string) => Promise<void> | void;
  btnStyle?: React.CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const colors = useColors();
  const styles = useStyles();

  const submit = async () => {
    if (!value.trim()) return;
    await onAdd(value.trim());
    setValue("");
    setOpen(false);
  };

  const close = () => {
    setOpen(false);
    setValue("");
  };

  return (
    <>
      <button
        style={{ ...styles.btn, fontSize: "0.75rem", ...btnStyle }}
        onClick={() => setOpen(true)}
      >
        {label}
      </button>
      {open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.25)",
          }}
          onClick={close}
        >
          <div
            style={{
              background: colors.bg,
              border: `1px solid ${colors.border}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              padding: "8px",
              display: "flex",
              gap: "4px",
              whiteSpace: "nowrap",
              borderRadius: "6px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              autoFocus
              placeholder={placeholder}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
                if (e.key === "Escape") close();
              }}
              style={{
                ...styles.input,
                fontSize: "0.875rem",
                padding: "0.35rem 0.5rem",
                width: "14rem",
              }}
            />
            <button
              style={{
                ...styles.btnPrimary,
                fontSize: "0.8rem",
                padding: "0.35rem 0.75rem",
              }}
              onClick={submit}
            >
              +
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export function HospitalsPage() {
  const { t } = useI18n();
  const dispatch = useCommand();
  const rpc = useRpc();
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState<{
    type: "region" | "hospital";
    id: string;
    name: string;
  } | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ kind: "blocked"; message: string } | { kind: "confirm"; entityType: "region" | "hospital"; id: string; name: string } | null>(null);
  const [editingRegion, setEditingRegion] = useState<string | null>(null);
  const [editingRegionName, setEditingRegionName] = useState("");
  const [dragHospitalId, setDragHospitalId] = useState<string | null>(null);
  const [dropTargetRegion, setDropTargetRegion] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const touchDragRef = useRef<{ hospitalId: string; startY: number } | null>(
    null,
  );
  const dropTargetRef = useRef<string | null>(null);
  const colors = useColors();
  const styles = useStyles();

  const { data: hospitalList } = useQuery(() => db.select().from(s.hospitals));

  const { data: regionList } = useQuery(() => db.select().from(s.regions));

  const activeRegions = (regionList || []).filter((r) => !r.archived);
  const archivedRegions = (regionList || []).filter((r) => r.archived);
  const activeHospitals = (hospitalList || []).filter((h) => !h.archived && !h.mergedInto);
  const { setSource: setMergeHospitalSource, modal: hospitalMergeModal } = useMerge(
    "hospital.merge", activeHospitals, "admin.mergeHospitalWarning", dispatch, t,
  );
  const archivedHospitals = (hospitalList || []).filter((h) => h.archived);
  const allRegions = activeRegions;
  const allHospitals = activeHospitals;
  const regionMap = new Map(
    [...activeRegions, ...archivedRegions].map((r) => [r.id, r.name || ""]),
  );

  // Filter by search
  const q = search.trim().toLowerCase();
  const matchingHospitals = q
    ? allHospitals.filter(
        (h) =>
          (h.name || "").toLowerCase().includes(q) ||
          (regionMap.get(h.regionId || "") || "").toLowerCase().includes(q),
      )
    : allHospitals;
  const matchingHospitalRegionIds = new Set(
    matchingHospitals.map((h) => h.regionId || "__none__"),
  );
  const matchingRegionsByName = q
    ? allRegions.filter((r) => (r.name || "").toLowerCase().includes(q))
    : [];

  // Group hospitals by region
  const byRegion = new Map<string, typeof allHospitals>();
  for (const h of matchingHospitals) {
    const key = h.regionId || "__none__";
    const list = byRegion.get(key) || [];
    list.push(h);
    byRegion.set(key, list);
  }

  // Archived hospitals grouped by region (only when showArchived)
  const archivedByRegion = new Map<string, typeof archivedHospitals>();
  if (showArchived) {
    const matchingArchived = q
      ? archivedHospitals.filter(
          (h) =>
            (h.name || "").toLowerCase().includes(q) ||
            (regionMap.get(h.regionId || "") || "").toLowerCase().includes(q),
        )
      : archivedHospitals;
    for (const h of matchingArchived) {
      const key = h.regionId || "__none__";
      const list = archivedByRegion.get(key) || [];
      list.push(h);
      archivedByRegion.set(key, list);
    }
  }

  // Visible regions: those with matching hospitals OR matching by name (show all when no search)
  const visibleRegions = allRegions.filter(
    (r) =>
      !q ||
      matchingHospitalRegionIds.has(r.id) ||
      matchingRegionsByName.some((mr) => mr.id === r.id) ||
      (showArchived && archivedByRegion.has(r.id)),
  );
  const noRegionHospitals = byRegion.get("__none__") || [];
  const noRegionArchivedHospitals = archivedByRegion.get("__none__") || [];

  const addRegion = async (name: string) => {
    const uuid = uuidv7();
    await dispatch("region.create", { uuid });
    await dispatch("region.setName", { uuid, name });
  };

  const addHospital = async (regionId: string, name: string) => {
    const uuid = uuidv7();
    await dispatch("hospital.create", { uuid });
    await dispatch("hospital.setName", { uuid, name });
    await dispatch("hospital.setRegion", { uuid, regionUuid: regionId });
  };

  const saveRegionName = async (id: string) => {
    if (editingRegionName.trim()) {
      await dispatch("region.setName", {
        uuid: id,
        name: editingRegionName.trim(),
      });
    }
    setEditingRegion(null);
  };

  // D&D handlers
  const handleDragStart = (hospitalId: string, e: React.DragEvent) => {
    setDragHospitalId(hospitalId);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOverRegion = (regionId: string, e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropTargetRegion !== regionId) setDropTargetRegion(regionId);
  };
  const handleDropOnRegion = async (
    targetRegionId: string,
    e: React.DragEvent,
  ) => {
    e.preventDefault();
    setDropTargetRegion(null);
    if (!dragHospitalId) return;
    const actual = targetRegionId === "__none__" ? "" : targetRegionId;
    await dispatch("hospital.setRegion", {
      uuid: dragHospitalId,
      regionUuid: actual,
    });
    setDragHospitalId(null);
  };
  const handleDragEnd = () => {
    setDragHospitalId(null);
    setDropTargetRegion(null);
  };

  // Touch D&D for tablet support
  useEffect(() => {
    const container = listRef.current;
    if (!container) return;

    const onTouchStart = (e: TouchEvent) => {
      const grip = (e.target as HTMLElement).closest(
        "[data-hospital-id]",
      ) as HTMLElement | null;
      if (!grip) return;
      const hospitalId = grip.dataset.hospitalId!;
      e.preventDefault();
      touchDragRef.current = { hospitalId, startY: e.touches[0].clientY };
      dropTargetRef.current = null;
      setDragHospitalId(hospitalId);
      setDropTargetRegion(null);

      const onTouchMove = (ev: TouchEvent) => {
        ev.preventDefault();
        const touch = ev.touches[0];
        const target = document.elementFromPoint(touch.clientX, touch.clientY);
        const regionEl = target?.closest(
          "[data-region-id]",
        ) as HTMLElement | null;
        const regionId = regionEl?.dataset.regionId || null;
        if (regionId !== dropTargetRef.current) {
          dropTargetRef.current = regionId;
          setDropTargetRegion(regionId);
        }
      };

      const onTouchEnd = () => {
        document.removeEventListener("touchmove", onTouchMove);
        document.removeEventListener("touchend", onTouchEnd);
        document.removeEventListener("touchcancel", onTouchEnd);
        const hospitalId = touchDragRef.current?.hospitalId;
        const targetRegionId = dropTargetRef.current;
        touchDragRef.current = null;
        dropTargetRef.current = null;
        setDragHospitalId(null);
        setDropTargetRegion(null);
        if (!hospitalId || !targetRegionId) return;
        const actual = targetRegionId === "__none__" ? "" : targetRegionId;
        dispatch("hospital.setRegion", {
          uuid: hospitalId,
          regionUuid: actual,
        });
      };

      document.addEventListener("touchmove", onTouchMove, { passive: false });
      document.addEventListener("touchend", onTouchEnd);
      document.addEventListener("touchcancel", onTouchEnd);
    };

    container.addEventListener("touchstart", onTouchStart, { passive: false });
    return () => container.removeEventListener("touchstart", onTouchStart);
  }, [dispatch]);

  const isDrop = (rId: string) => dragHospitalId && dropTargetRegion === rId;
  const regionHeaderStyle = (rId: string): React.CSSProperties => ({
    padding: "0.5rem 0.75rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontWeight: 600,
    fontSize: "0.85rem",
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    background: isDrop(rId) ? colors.primary + "30" : colors.bgAlt,
    borderLeft: isDrop(rId)
      ? `3px solid ${colors.primary}`
      : "3px solid transparent",
    transition: "background 0.15s, border-left 0.15s",
    cursor: "default",
  });

  const hospitalRowStyle = (hId: string): React.CSSProperties => ({
    padding: "0.375rem 0.75rem 0.375rem 1.5rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "0.85rem",
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    opacity: dragHospitalId === hId ? 0.3 : 1,
    background: colors.bg,
  });

  const requestArchive = (
    type: "region" | "hospital",
    id: string,
    name: string,
  ) => {
    setConfirmArchive({ type, id, name });
  };

  const confirmAndArchive = async () => {
    if (!confirmArchive) return;
    const { type, id } = confirmArchive;
    await dispatch(type === "region" ? "region.archive" : "hospital.archive", {
      uuid: id,
    });
    setConfirmArchive(null);
  };


  const requestDelete = async (entityType: "region" | "hospital", entityId: string, name: string) => {
    try {
      const result = await rpc<{ deletable: boolean; reason?: string }>({ type: "canDelete", entityType, entityId });
      if (result.deletable) {
        setDeleteModal({ kind: "confirm", entityType, id: entityId, name });
      } else {
        setDeleteModal({ kind: "blocked", message: t("common.deleteBlocked", { reason: result.reason || "" }) });
      }
    } catch { /* ignore */ }
  };

  const confirmDelete = async () => {
    if (deleteModal?.kind !== "confirm") return;
    const { entityType, id } = deleteModal;
    setDeleteModal(null);
    try {
      await rpc({ type: "deleteEntity", entityType, entityId: id });
    } catch (e) {
      setDeleteModal({ kind: "blocked", message: (e as any)?.message || "Delete failed" });
    }
  };

  const renderRegionGroup = (
    region: { id: string; name: string | null } | null,
    hospitals: typeof allHospitals,
  ) => {
    const rId = region?.id || "__none__";
    const rName = region?.name || t("insights.noRegion");
    const archivedInGroup = archivedByRegion.get(rId) || [];
    return (
      <div key={rId}>
        <div
          data-region-id={rId}
          style={regionHeaderStyle(rId)}
          onDragOver={(e) => handleDragOverRegion(rId, e)}
          onDragLeave={() => {
            if (dropTargetRegion === rId) setDropTargetRegion(null);
          }}
          onDrop={(e) => handleDropOnRegion(rId, e)}
        >
          {editingRegion === rId && region ? (
            <input
              autoFocus
              value={editingRegionName}
              onChange={(e) => setEditingRegionName(e.target.value)}
              onBlur={() => saveRegionName(rId)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveRegionName(rId);
                if (e.key === "Escape") setEditingRegion(null);
              }}
              style={{
                ...styles.input,
                fontWeight: 600,
                fontSize: "0.85rem",
                padding: "0 0.25rem",
                width: "12rem",
              }}
            />
          ) : (
            <span
              style={{ cursor: region ? "text" : "default" }}
              onDoubleClick={() => {
                if (region) {
                  setEditingRegion(rId);
                  setEditingRegionName(region.name || "");
                }
              }}
            >
              {rName}
            </span>
          )}
          <span style={{ flex: 1 }} />
          {region && !q && (
            <PopoverAdd
              label={`+ ${t("case.hospital")}`}
              placeholder={t("admin.name")}
              onAdd={(name) => addHospital(rId, name)}
            />
          )}
          {region && (
            <>
              <button
                style={{ ...styles.btn, fontSize: "0.7rem", color: colors.danger, padding: "0 0.25rem" }}
                onClick={() => requestArchive("region", rId, region.name || rId.slice(-6))}
              >
                {t("case.archive")}
              </button>
              <button
                style={{ ...styles.btn, fontSize: "0.7rem", color: colors.danger, padding: "0 0.25rem" }}
                onClick={() => requestDelete("region", rId, region.name || rId.slice(-6))}
              >
                {t("common.delete")}
              </button>
            </>
          )}
        </div>
        {hospitals.map((h) => (
          <div key={h.id} style={hospitalRowStyle(h.id)}>
            <span
              data-hospital-id={h.id}
              draggable
              onDragStart={(e) => handleDragStart(h.id, e)}
              onDragEnd={handleDragEnd}
              style={{
                color: colors.textMuted,
                cursor: "grab",
                userSelect: "none",
              }}
            >
              ⠿
            </span>
            <span style={{ flex: 1 }}>{h.name || "—"}</span>
            <button
              style={{ ...styles.btn, fontSize: "0.7rem", color: colors.textMuted, padding: "0 0.25rem", marginRight: "4px" }}
              onClick={() => setMergeHospitalSource({ id: h.id, name: h.name || h.id.slice(-6) })}
            >
              {t("admin.merge")}
            </button>
            <button
              style={{ ...styles.btn, fontSize: "0.7rem", color: colors.danger, padding: "0 0.25rem", marginRight: "4px" }}
              onClick={() => requestArchive("hospital", h.id, h.name || h.id.slice(-6))}
            >
              {t("case.archive")}
            </button>
            <button
              style={{ ...styles.btn, fontSize: "0.7rem", color: colors.danger, padding: "0 0.25rem" }}
              onClick={() => requestDelete("hospital", h.id, h.name || h.id.slice(-6))}
            >
              {t("common.delete")}
            </button>
          </div>
        ))}
        {showArchived &&
          archivedInGroup.map((h) => (
            <div
              key={h.id}
              style={{
                ...hospitalRowStyle(h.id),
                opacity: 0.45,
                cursor: "default",
              }}
            >
              <span style={{ color: colors.textMuted, userSelect: "none" }}>
                ⠿
              </span>
              <span style={{ flex: 1, textDecoration: "line-through" }}>
                {h.name || "—"}
              </span>
              <button
                style={{
                  ...styles.btn,
                  fontSize: "0.7rem",
                  color: colors.primary,
                  padding: "0 0.25rem",
                }}
                onClick={() => dispatch("hospital.unarchive", { uuid: h.id })}
              >
                {t("common.unarchive")}
              </button>
            </div>
          ))}
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          padding: "0.5rem 0.75rem",
          display: "flex",
          gap: "0.5rem",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <PopoverAdd
          label={`+ ${t("admin.region")}`}
          placeholder={t("admin.name")}
          onAdd={addRegion}
          btnStyle={{ padding: "0.25rem 0.5rem" }}
        />
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "0.8rem",
            color: colors.textMuted,
          }}
        >
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          {t("common.showArchived")}
        </label>
        <input
          placeholder={t("app.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...styles.input, maxWidth: "16rem", marginLeft: "auto" }}
        />
      </div>
      <div ref={listRef} style={{ flex: 1, overflow: "auto", minWidth: 0 }}>
        {visibleRegions.map((r) =>
          renderRegionGroup(r, byRegion.get(r.id) || []),
        )}
        {(noRegionHospitals.length > 0 ||
          (showArchived && noRegionArchivedHospitals.length > 0)) &&
          renderRegionGroup(null, noRegionHospitals)}
        {showArchived && archivedRegions.length > 0 && (
          <>
            {archivedRegions
              .filter(
                (r) =>
                  !q ||
                  (r.name || "").toLowerCase().includes(q) ||
                  archivedByRegion.has(r.id),
              )
              .map((r) => (
                <div key={r.id}>
                  <div style={{ ...regionHeaderStyle(r.id), opacity: 0.45 }}>
                    <span style={{ textDecoration: "line-through" }}>
                      {r.name || r.id.slice(-6)}
                    </span>
                    <span style={{ flex: 1 }} />
                    <button
                      style={{
                        ...styles.btn,
                        fontSize: "0.7rem",
                        color: colors.primary,
                        padding: "0 0.25rem",
                      }}
                      onClick={() =>
                        dispatch("region.unarchive", { uuid: r.id })
                      }
                    >
                      {t("common.unarchive")}
                    </button>
                  </div>
                  {(archivedByRegion.get(r.id) || []).map((h) => (
                    <div
                      key={h.id}
                      style={{
                        ...hospitalRowStyle(h.id),
                        opacity: 0.45,
                        cursor: "default",
                      }}
                    >
                      <span
                        style={{ color: colors.textMuted, userSelect: "none" }}
                      >
                        ⠿
                      </span>
                      <span style={{ flex: 1, textDecoration: "line-through" }}>
                        {h.name || "—"}
                      </span>
                      <button
                        style={{
                          ...styles.btn,
                          fontSize: "0.7rem",
                          color: colors.primary,
                          padding: "0 0.25rem",
                        }}
                        onClick={() =>
                          dispatch("hospital.unarchive", { uuid: h.id })
                        }
                      >
                        {t("common.unarchive")}
                      </button>
                    </div>
                  ))}
                </div>
              ))}
          </>
        )}
      </div>
      {hospitalMergeModal}
      {deleteModal?.kind === "blocked" && <ConfirmModal message={deleteModal.message} onCancel={() => setDeleteModal(null)} />}
      {deleteModal?.kind === "confirm" && <ConfirmModal message={t("common.deleteConfirm", { name: deleteModal.name })} onCancel={() => setDeleteModal(null)} onConfirm={confirmDelete} />}
      {confirmArchive && (
        <ConfirmModal
          message={t("common.archiveConfirm", { name: confirmArchive.name })}
          onCancel={() => setConfirmArchive(null)}
          onConfirm={confirmAndArchive}
          confirmLabel={t("case.archive")}
        />
      )}
    </div>
  );
}

function ParentUnitCell({
  unit,
  allUnits,
  onAttach,
  onDetach,
  allowedParentIds,
}: {
  unit: UnitListItem;
  allUnits: UnitListItem[];
  onAttach: (parentId: string) => void;
  onDetach: () => void;
  allowedParentIds?: string[];
}) {
  const [picking, setPicking] = useState(false);
  const colors = useColors();
  const styles = useStyles();

  const parentUnit = unit.parentUnitId
    ? allUnits.find((u) => u.id === unit.parentUnitId)
    : null;

  const eligibleParents = allUnits
    .filter((u) => u.id !== unit.id)
    .filter((u) => !allowedParentIds || allowedParentIds.includes(u.id));

  if (picking) {
    return (
      <select
        autoFocus
        style={{ ...styles.select, fontSize: "0.8rem", padding: "2px 4px" }}
        defaultValue=""
        onBlur={() => setPicking(false)}
        onChange={(e) => {
          if (e.target.value) onAttach(e.target.value);
          setPicking(false);
        }}
      >
        <option value="" disabled>— select parent —</option>
        {eligibleParents.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name || u.id.slice(-6)}
            </option>
          ))}
      </select>
    );
  }

  if (parentUnit) {
    return (
      <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <span
          style={{ cursor: "pointer", color: colors.primary }}
          onClick={() => setPicking(true)}
        >
          {parentUnit.name || parentUnit.id.slice(-6)}
        </span>
        <span
          style={{ cursor: "pointer", color: colors.textMuted, fontSize: "0.75rem" }}
          onClick={onDetach}
          title="Remove parent"
        >
          ×
        </span>
      </span>
    );
  }

  return (
    <span
      style={{ cursor: "pointer", color: colors.textMuted, fontSize: "0.8rem" }}
      onClick={() => setPicking(true)}
    >
      + set parent
    </span>
  );
}

export function UnitsPage() {
  const { t } = useI18n();
  const dispatch = useCommand();
  const rpc = useRpc();
  const { online } = useStore();
  const { user } = useAuth();
  const isMedicRole2 = user?.role === "medicRole2";
  const [name, setName] = useState("");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ kind: "blocked"; message: string } | { kind: "confirm"; id: string; name: string } | null>(null);
  const [unitItems, setUnitItems] = useState<UnitListItem[]>([]);
  const colors = useColors();
  const styles = useStyles();
  const listVersion = useListVersion();

  // Fetch units with embedded advisors from server RPC
  const refresh = useCallback(() => {
    if (!online) return;
    rpc<{ units: UnitListItem[] }>({ type: "listUnits" })
      .then((data) => setUnitItems(data.units || []))
      .catch(() => {});
  }, [rpc, online]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Re-fetch when server broadcasts a list invalidation
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    if (listVersion > 0) refreshRef.current();
  }, [listVersion]);

  const allItems = unitItems;
  const activeItems = allItems.filter((r) => !r.archived);
  const archivedItems = allItems.filter((r) => r.archived);
  const { setSource: setMergeSource, modal: unitMergeModal } = useMerge(
    "unit.merge", activeItems, "admin.mergeUnitWarning", dispatch, t,
    (sourceId) => setUnitItems((prev) => prev.filter((u) => u.id !== sourceId)),
  );

  const matchesSearch = (r: any) =>
    !search.trim() ||
    (r.name || "").toLowerCase().includes(search.toLowerCase());

  const filtered = activeItems.filter(matchesSearch);
  const filteredArchived = archivedItems.filter(matchesSearch);

  const add = async () => {
    if (!name.trim()) return;
    const existing = activeItems.find(
      (u) => (u.name || "").trim().toLowerCase() === name.trim().toLowerCase(),
    );
    if (existing) {
      setDuplicateWarning(existing.name || name.trim());
      return;
    }
    const uuid = uuidv7();
    setUnitItems((prev) => [
      ...prev,
      { id: uuid, name: name.trim(), archived: 0, advisors: [] },
    ]);
    await dispatch("unit.create", { uuid });
    await dispatch("unit.setName", { uuid, name: name.trim() });
    setName("");
  };



  const confirmAndArchive = async () => {
    if (!confirmArchive) return;
    setUnitItems((prev) =>
      prev.map((u) => (u.id === confirmArchive.id ? { ...u, archived: 1 } : u)),
    );
    await dispatch("unit.archive", { uuid: confirmArchive.id });
    setConfirmArchive(null);
  };

  const requestDelete = async (unitId: string, name: string) => {
    try {
      const result = await rpc<{ deletable: boolean; reason?: string }>({ type: "canDelete", entityType: "unit", entityId: unitId });
      if (result.deletable) {
        setDeleteModal({ kind: "confirm", id: unitId, name });
      } else {
        setDeleteModal({ kind: "blocked", message: t("common.deleteBlocked", { reason: result.reason || "" }) });
      }
    } catch { /* ignore */ }
  };

  const confirmDelete = async () => {
    if (deleteModal?.kind !== "confirm") return;
    const { id } = deleteModal;
    setDeleteModal(null);
    try {
      await rpc({ type: "deleteEntity", entityType: "unit", entityId: id });
      setUnitItems((prev: UnitListItem[]) => prev.filter((u: UnitListItem) => u.id !== id));
    } catch (e) {
      setDeleteModal({ kind: "blocked", message: (e as any)?.message || "Delete failed" });
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
        style={{
          padding: "8px 12px",
          display: "flex",
          gap: "4px",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {!isMedicRole2 && (
          <>
            <input
              placeholder={t("admin.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ ...styles.input, maxWidth: "300px" }}
            />
            <button type="submit" style={styles.btnPrimary}>
              {t("admin.create")}
            </button>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "0.8rem",
                color: colors.textMuted,
                marginLeft: "0.5rem",
              }}
            >
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              {t("common.showArchived")}
            </label>
          </>
        )}
        <input
          placeholder={t("app.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...styles.input, maxWidth: "200px", marginLeft: isMedicRole2 ? undefined : "auto" }}
        />
      </form>
      <div style={{ flex: 1, overflow: "auto", minWidth: 0 }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>{t("admin.name")}</th>
              <th style={styles.th}>{t("admin.parentUnit")}</th>
              {!isMedicRole2 && <th style={styles.th}>{t("role.tacticalMedicalOfficer")}</th>}
              {!isMedicRole2 && <th style={styles.th}></th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id}>
                <td style={styles.td}>{row.name || "—"}</td>
                <td style={{ ...styles.td, fontSize: "0.85rem" }}>
                  <ParentUnitCell
                    unit={row}
                    allUnits={filtered}
                    allowedParentIds={isMedicRole2 ? (user?.unitIds ?? []) : undefined}
                    onAttach={async (parentId) => {
                      setUnitItems((prev) =>
                        prev.map((u) =>
                          u.id === row.id ? { ...u, parentUnitId: parentId } : u,
                        ),
                      );
                      await dispatch("unit.attachToParent", {
                        uuid: parentId,
                        parentUnitId: parentId,
                        childUnitId: row.id,
                      });
                    }}
                    onDetach={async () => {
                      const parentId = row.parentUnitId!;
                      setUnitItems((prev) =>
                        prev.map((u) =>
                          u.id === row.id ? { ...u, parentUnitId: null } : u,
                        ),
                      );
                      await dispatch("unit.detachFromParent", {
                        uuid: parentId,
                        parentUnitId: parentId,
                        childUnitId: row.id,
                      });
                    }}
                  />
                </td>
                {!isMedicRole2 && (
                <td style={{ ...styles.td, fontSize: "0.8rem" }}>
                  {row.advisors.length > 0
                    ? row.advisors.map((a, i) => (
                        <span key={a.id}>
                          {i > 0 && ", "}
                          <Link
                            to={`/users/${a.id}`}
                            style={{ color: colors.primary }}
                          >
                            {a.name || a.email}
                          </Link>
                          {a.name && (
                            <span
                              style={{
                                color: colors.textMuted,
                                marginLeft: "4px",
                              }}
                            >
                              ({a.email})
                            </span>
                          )}
                        </span>
                      ))
                    : "—"}
                </td>
                )}
                {!isMedicRole2 && (
                  <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                    <button
                      style={{ ...styles.btn, fontSize: "0.75rem", color: colors.textMuted, marginRight: "4px" }}
                      onClick={() => setMergeSource({ id: row.id, name: row.name || row.id.slice(-6) })}
                    >
                      {t("admin.merge")}
                    </button>
                    <button
                      style={{ ...styles.btn, fontSize: "0.75rem", color: colors.danger, marginRight: "4px" }}
                      onClick={() => setConfirmArchive({ id: row.id, name: row.name || row.id.slice(-6) })}
                    >
                      {t("case.archive")}
                    </button>
                    <button
                      style={{ ...styles.btn, fontSize: "0.75rem", color: colors.danger }}
                      onClick={() => requestDelete(row.id, row.name || row.id.slice(-6))}
                    >
                      {t("common.delete")}
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {showArchived &&
              filteredArchived.map((row) => (
                <tr key={row.id} style={{ opacity: 0.45 }}>
                  <td style={{ ...styles.td, textDecoration: "line-through" }}>
                    {row.name || "—"}
                  </td>
                  <td
                    style={{
                      ...styles.td,
                      fontSize: "0.8rem",
                      textDecoration: "line-through",
                    }}
                  >
                    {row.advisors.length > 0
                      ? row.advisors.map((a, i) => (
                          <span key={a.id}>
                            {i > 0 && ", "}
                            <Link
                              to={`/users/${a.id}`}
                              style={{ color: colors.primary }}
                            >
                              {a.name || a.email}
                            </Link>
                            {a.name && (
                              <span
                                style={{
                                  color: colors.textMuted,
                                  marginLeft: "4px",
                                }}
                              >
                                ({a.email})
                              </span>
                            )}
                          </span>
                        ))
                      : "—"}
                  </td>
                  <td style={styles.td}>
                    <button
                      style={{
                        ...styles.btn,
                        fontSize: "0.75rem",
                        color: colors.primary,
                      }}
                      onClick={() =>
                        dispatch("unit.unarchive", { uuid: row.id })
                      }
                    >
                      {t("common.unarchive")}
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {deleteModal?.kind === "blocked" && <ConfirmModal message={deleteModal.message} onCancel={() => setDeleteModal(null)} />}
      {deleteModal?.kind === "confirm" && <ConfirmModal message={t("common.deleteConfirm", { name: deleteModal.name })} onCancel={() => setDeleteModal(null)} onConfirm={confirmDelete} />}
      {duplicateWarning && <ConfirmModal message={t("admin.duplicateUnit", { name: duplicateWarning })} onCancel={() => setDuplicateWarning(null)} />}
      {unitMergeModal}
      {confirmArchive && (
        <ConfirmModal
          message={t("common.archiveConfirm", { name: confirmArchive.name })}
          onCancel={() => setConfirmArchive(null)}
          onConfirm={confirmAndArchive}
          confirmLabel={t("case.archive")}
        />
      )}
    </div>
  );
}

interface AuditEvent {
  id: string;
  type: string;
  entityId: string;
  payload: Record<string, unknown>;
  userId: string;
  sessionId: string;
  clientTimestamp: number;
  serverTimestamp: number;
}


type AuditLookups = {
  users: Map<string, { email: string; name: string | null }>;
  hospitals: Map<string, { name: string | null }>;
  regions: Map<string, { name: string | null }>;
  units: Map<string, { name: string | null }>;
};

function describeAuditEvent(
  ev: AuditEvent,
  L: AuditLookups,
  t: (key: string, params?: Record<string, string>) => string,
): string {
  const p = ev.payload;
  const [domain, action] = ev.type.split(".", 2);
  const dl = t(`domain.${domain}`).toLowerCase();
  const hn = (id: unknown) =>
    typeof id === "string" ? L.hospitals.get(id)?.name || id.slice(0, 8) : "";
  const rn = (id: unknown) =>
    typeof id === "string" ? L.regions.get(id)?.name || id.slice(0, 8) : "";
  const un = (id: unknown) =>
    typeof id === "string" ? L.units.get(id)?.name || id.slice(0, 8) : "";
  const usr = (id: unknown) => {
    if (typeof id !== "string") return "";
    const u = L.users.get(id);
    if (!u) return id;
    return u.name ? `${u.name} (${u.email})` : u.email;
  };
  const ts = (v: unknown) =>
    typeof v === "number" ? formatDateTime(v) : String(v);
  const boolKeys = (obj: unknown) =>
    Object.entries((obj || {}) as Record<string, unknown>)
      .filter(([, v]) => v)
      .map(([k]) => k.replace(/([A-Z])/g, " $1").trim())
      .join(", ") || t("audit.none");

  // Create / delete / archive
  if (action === "create") {
    if (ev.type === "user.create")
      return t("auditAction.createdUser", { email: String(p.email) });
    if (ev.type === "patient.create")
      return t("auditAction.createdPatient", { unit: un(p.unitUuid) });
    if (ev.type === "attachment.create")
      return t("auditAction.addedAttachment", { type: String(p.mimeType) });
    if (ev.type === "session.create") return t("auditAction.startedSession");
    if ("caseUuid" in p) return t("auditAction.addedRecord", { domain: dl });
    return t("auditAction.created", { domain: dl });
  }
  if (action === "deleted") {
    if (ev.type === "user.deleted") return t("auditAction.deletedUser", { name: String(p.name || ""), email: String(p.email || "") });
    if (ev.type === "unit.deleted") return t("auditAction.deletedUnit", { name: String(p.name || "") });
    return t("auditAction.deleted", { domain: dl });
  }
  if (action === "archive") return t("auditAction.archived", { domain: dl });
  if (action === "unarchive")
    return t("auditAction.unarchived", { domain: dl });

  // Named actions
  switch (action) {
    case "sendInvite":
      return t("auditAction.sentInvite");
    case "sawEvents":
      return t("auditAction.sawEvents", {
        count: String((p.eventUuids as string[])?.length || 0),
      });
    case "refresh":
      return t("auditAction.refreshedSession");
    case "destroy":
      return t("auditAction.terminatedSession");
    case "erasePersonalData":
      return t("auditAction.erasedData", { reason: String(p.reason) });
    case "merge":
      return t("auditAction.mergedPatients");
    case "wipeDatabase":
      return t("auditAction.wipedDatabase", { reason: String(p.reason) });
    case "setData":
      return t("auditAction.uploadedAttachment");
    case "attachHospital":
      return t("auditAction.addedHospital", { name: hn(p.hospitalUuid) });
    case "detachHospital":
      return t("auditAction.removedHospital", { name: hn(p.hospitalUuid) });
    case "attachUnit":
      return t("auditAction.addedUnit", { name: un(p.unitUuid) });
    case "detachUnit":
      return t("auditAction.removedUnit", { name: un(p.unitUuid) });
    case "setName":
      return t("auditAction.renamed", { name: String(p.name) });
    case "setRole":
      return t("auditAction.changedRole", { role: String(p.role) });
    case "setEmail":
      return t("auditAction.changedEmail", { email: String(p.email) });
    case "setPriorCareItem":
    case "setR1CareItem":
    case "setR2CareItem":
    case "setDischargeEquipmentItem":
    case "setR2DischargeEquipmentItem":
    case "setBreathingSoundsItem":
    case "setTraumaTypeItem":
    case "setConditionTypeItem":
    case "setCbrnItem":
    case "setSurgeryTypeItem":
    case "setLimbDeformationItem":
    case "setNeuroDeficitItem":
      return p.value
        ? t("auditAction.enabled", { field: String(p.key) })
        : t("auditAction.disabled", { field: String(p.key) });
    case "setTraumaBurnItem":
    case "setTraumaZoneItem":
      return p.value
        ? t("auditAction.enabled", { field: String(p.zone) })
        : t("auditAction.disabled", { field: String(p.zone) });
    case "addInjuryMark":
      return t("auditAction.enabled", {
        field: `injury mark (${Number(p.x).toFixed(0)}%, ${Number(p.y).toFixed(0)}%)`,
      });
    case "removeInjuryMark":
      return t("auditAction.disabled", {
        field: `injury mark ${String(p.markId).slice(0, 8)}`,
      });
  }

  // Generic "set" handler
  if (action?.startsWith("set")) {
    const field = action
      .slice(3)
      .replace(/([A-Z])/g, " $1")
      .trim()
      .toLowerCase();
    const vk = Object.keys(p).find((k) => k !== "uuid");
    if (vk) {
      const v = p[vk];
      if (vk === "hospitalUuid")
        return t("auditAction.changedField", { field, value: hn(v) });
      if (vk === "regionUuid")
        return t("auditAction.changedField", { field, value: rn(v) });
      if (vk === "unitUuid")
        return t("auditAction.changedField", { field, value: un(v) });
      if (vk === "userUuid")
        return t("auditAction.changedField", { field, value: usr(v) });
      if (vk === "patientUuid") return t("audit.assignedPatient");
      if (vk === "timestamp" || vk === "capturedAt")
        return t("auditAction.changedField", { field, value: ts(v) });
      if (typeof v === "boolean")
        return v
          ? t("auditAction.enabled", { field })
          : t("auditAction.disabled", { field });
      if (typeof v === "number")
        return t("auditAction.changedField", { field, value: String(v) });
      if (typeof v === "string")
        return t("auditAction.changedFieldTo", { field, value: v });
      if (Array.isArray(v))
        return t("auditAction.updatedFieldDetails", {
          field,
          details: v.join(", "),
        });
      if (typeof v === "object" && v)
        return t("auditAction.updatedFieldDetails", {
          field,
          details: boolKeys(v),
        });
    }
    return t("auditAction.updatedField", { field });
  }

  return ev.type;
}

function formatAuditDetail(
  key: string,
  val: unknown,
  L: AuditLookups,
  t: (key: string, params?: Record<string, string>) => string,
): { label: string; value: string; link?: string } | null {
  if (key === "uuid" || key === "sha256") return null;
  let label = key
    .replace(/Uuid$/, "")
    .replace(/([A-Z])/g, " $1")
    .trim();
  label = label.charAt(0).toUpperCase() + label.slice(1);
  let display: string;
  let link: string | undefined;
  if (key === "hospitalUuid") {
    label = t("domain.hospital");
    display =
      L.hospitals.get(val as string)?.name || (val as string).slice(0, 8);
  } else if (key === "regionUuid") {
    label = t("domain.region");
    display = L.regions.get(val as string)?.name || (val as string).slice(0, 8);
  } else if (key === "unitUuid") {
    label = t("domain.unit");
    display = L.units.get(val as string)?.name || (val as string).slice(0, 8);
  } else if (key === "userUuid") {
    label = t("domain.user");
    const u = L.users.get(val as string);
    display = u
      ? u.name
        ? `${u.name} (${u.email})`
        : u.email
      : (val as string);
    link = `/users/${val}`;
  } else if (key === "caseUuid") {
    label = t("domain.case");
    display = typeof val === "string" ? val.slice(0, 8) + "..." : String(val);
    link = `/cases/${val}`;
  } else if (key === "patientUuid") {
    label = t("domain.patient");
    display = typeof val === "string" ? val.slice(0, 8) + "..." : String(val);
    link = `/patients/${val}`;
  } else if (key === "targetUuid") {
    label = t("domain.patient");
    display = typeof val === "string" ? val.slice(0, 8) + "..." : String(val);
    link = `/patients/${val}`;
  } else if (key === "eventUuids") {
    label = t("audit.type");
    display = t("audit.total", { count: String((val as string[]).length) });
  } else if (key === "timestamp" || key === "capturedAt")
    display = typeof val === "number" ? formatDateTime(val) : String(val);
  else if (typeof val === "boolean")
    display = val ? t("common.yes") : t("common.no");
  else if (Array.isArray(val)) display = val.join(", ");
  else if (typeof val === "object" && val !== null) {
    const entries = Object.entries(val as Record<string, unknown>).filter(
      ([, v]) => v,
    );
    if (entries.length === 0) display = t("audit.none");
    else if (entries.every(([, v]) => v === true))
      display = entries
        .map(([k]) => k.replace(/([A-Z])/g, " $1").trim())
        .join(", ");
    else
      display = entries
        .map(([k, v]) => `${k.replace(/([A-Z])/g, " $1").trim()}: ${v}`)
        .join(", ");
  } else display = String(val ?? "");
  return { label, value: display, link };
}

function resolveEntityInfo(
  domain: string,
  entityId: string,
  payload: Record<string, unknown>,
  L: AuditLookups,
): { name: string | null; link: string | null } {
  switch (domain) {
    case "user": {
      const u = L.users.get(entityId);
      return {
        name: u ? (u.name ? `${u.name} (${u.email})` : u.email) : null,
        link: `/users/${entityId}`,
      };
    }
    case "case":
      return { name: null, link: `/cases/${entityId}` };
    case "patient":
      return { name: null, link: `/patients/${entityId}` };
    case "hospital":
      return { name: L.hospitals.get(entityId)?.name || null, link: null };
    case "region":
      return { name: L.regions.get(entityId)?.name || null, link: null };
    case "unit":
      return { name: L.units.get(entityId)?.name || null, link: null };
    default: {
      const caseUuid = payload.caseUuid as string | undefined;
      return { name: null, link: caseUuid ? `/cases/${caseUuid}` : null };
    }
  }
}

function formatEventTypeLabel(
  type: string,
  t: (key: string) => string,
): string {
  const [d, a] = type.split(".", 2);
  const dLabel = t(`domain.${d}`);
  const aLabel = (a || "")
    .replace(/([A-Z])/g, " $1")
    .trim()
    .toLowerCase();
  return `${dLabel}: ${aLabel}`;
}

export function AuditPage() {
  const { t } = useI18n();
  const colors = useColors();
  const domainColors = useDomainColors();
  const styles = useStyles();
  const { rpc, online } = useStore();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [eventType, setEventType] = useState("");
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const listVersion = useListVersion();
  // User lookup via RPC (local DB lacks name/picture from OAuth)
  const [rpcUsers, setRpcUsers] = useState<UserListItem[]>([]);
  useEffect(() => {
    if (!online) return;
    rpc<{ users: UserListItem[] }>({ type: "listUsers" })
      .then((data) => setRpcUsers(data.users || []))
      .catch(() => {});
  }, [rpc, online, listVersion]);
  const userMap = new Map(rpcUsers.map((u) => [u.id, u]));
  const { data: hospitalList } = useQuery(() => db.select().from(s.hospitals));
  const hospitalMap = new Map((hospitalList || []).map((h) => [h.id, h]));
  const { data: regionList } = useQuery(() => db.select().from(s.regions));
  const regionMap = new Map((regionList || []).map((r) => [r.id, r]));
  const { data: unitList } = useQuery(() => db.select().from(s.units));
  const unitMap = new Map((unitList || []).map((u) => [u.id, u]));
  const lookups: AuditLookups = {
    users: userMap,
    hospitals: hospitalMap,
    regions: regionMap,
    units: unitMap,
  };

  // Fetch event types via WS RPC (wait for connection)
  useEffect(() => {
    if (!online) return;
    rpc<{ types: string[] }>({ type: "auditTypes" })
      .then((data) => setEventTypes(data.types || []))
      .catch(() => {});
  }, [rpc, online]);

  const fetchEvents = useCallback(
    async (beforeTs?: number) => {
      if (!online) return;
      setLoading(true);
      try {
        const data = await rpc<{
          events: AuditEvent[];
          hasMore: boolean;
          totalCount: number;
        }>({
          type: "audit",
          search: search || undefined,
          eventType: eventType || undefined,
          beforeTs,
          limit: 50,
        });

        if (beforeTs) {
          setEvents((prev) => [...prev, ...data.events]);
        } else {
          setEvents(data.events);
        }
        setHasMore(data.hasMore);
        setTotalCount(data.totalCount);
      } catch (e) {
        console.error("Audit fetch error:", e);
      } finally {
        setLoading(false);
      }
    },
    [rpc, online, search, eventType],
  );

  // Initial load and on filter change (wait for connection)
  useEffect(() => {
    if (online) fetchEvents();
  }, [fetchEvents, online]);

  // Re-fetch when server broadcasts a list invalidation
  const fetchEventsRef = useRef(fetchEvents);
  fetchEventsRef.current = fetchEvents;
  useEffect(() => {
    if (listVersion > 0) fetchEventsRef.current();
  }, [listVersion]);

  const loadMore = () => {
    if (events.length > 0) {
      fetchEvents(events[events.length - 1].serverTimestamp);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "8px 12px",
          display: "flex",
          gap: "8px",
          alignItems: "center",
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <input
          placeholder={t("audit.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...styles.input, maxWidth: "200px" }}
        />
        <select
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
          style={{ ...styles.select, maxWidth: "240px" }}
        >
          <option value="">{t("audit.allTypes")}</option>
          {eventTypes.map((type) => (
            <option key={type} value={type}>
              {formatEventTypeLabel(type, t)}
            </option>
          ))}
        </select>
        <span style={{ color: colors.textMuted }}>
          {t("audit.total", { count: String(totalCount) })}
        </span>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        {events.map((ev) => {
          const [domain] = ev.type.split(".", 2);
          const di = {
            label: t(`domain.${domain}`),
            color: domainColors[domain] || colors.textMuted,
          };
          const summary = describeAuditEvent(ev, lookups, t);
          const entity = resolveEntityInfo(
            domain,
            ev.entityId,
            ev.payload,
            lookups,
          );
          const details = Object.entries(ev.payload)
            .map(([k, v]) => formatAuditDetail(k, v, lookups, t))
            .filter(
              (d): d is { label: string; value: string; link?: string } =>
                d !== null,
            );

          return (
            <div
              key={ev.id}
              style={{
                padding: "10px 14px",
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "2px",
                }}
              >
                <span style={badge(di.color)}>{di.label}</span>
                {entity.name && entity.link ? (
                  <Link
                    to={entity.link}
                    style={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      color: colors.primary,
                    }}
                  >
                    {entity.name}
                  </Link>
                ) : entity.name ? (
                  <span
                    style={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      color: colors.text,
                    }}
                  >
                    {entity.name}
                  </span>
                ) : entity.link ? (
                  <Link
                    to={entity.link}
                    style={{ fontSize: "0.85rem", color: colors.primary }}
                  >
                    {t("audit.view")}
                  </Link>
                ) : null}
                <span
                  style={{
                    marginLeft: "auto",
                    color: colors.textMuted,
                    fontSize: "0.8rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatDateTime(ev.clientTimestamp)}
                  {ev.clientTimestamp !== ev.serverTimestamp && (
                    <span style={{ opacity: 0.6 }}>
                      {" "}
                      ({formatDateTime(ev.serverTimestamp)})
                    </span>
                  )}
                </span>
              </div>
              <div style={{ color: colors.text, margin: "2px 0" }}>
                {ev.userId === "00000000-0000-0000-0000-000000000000" ? (
                  <span style={{ fontStyle: "italic" }}>
                    {t("audit.system")}
                  </span>
                ) : userMap.get(ev.userId) ? (
                  <Link
                    to={`/users/${ev.userId}`}
                    style={{ fontWeight: 500, color: colors.primary }}
                  >
                    {userMap.get(ev.userId)!.name
                      ? `${userMap.get(ev.userId)!.name} (${userMap.get(ev.userId)!.email})`
                      : userMap.get(ev.userId)!.email}
                  </Link>
                ) : (
                  <span style={{ fontWeight: 500 }}>{ev.userId}</span>
                )}{" "}
                {summary[0].toLowerCase() + summary.slice(1)}
              </div>
              {details.length > 0 && (
                <div
                  style={{
                    marginTop: "4px",
                    paddingTop: "4px",
                    borderTop: `1px solid ${colors.border}`,
                    fontSize: "0.8rem",
                    color: colors.textMuted,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "2px 14px",
                  }}
                >
                  {details.map((d, i) => (
                    <span key={i}>
                      <b>{d.label}:</b>{" "}
                      {d.link ? (
                        <Link to={d.link} style={{ color: colors.primary }}>
                          {d.value}
                        </Link>
                      ) : (
                        d.value
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {loading && (
          <div
            style={{
              padding: "12px",
              textAlign: "center",
              color: colors.textMuted,
            }}
          >
            {t("app.loading")}
          </div>
        )}

        {hasMore && !loading && (
          <div style={{ padding: "12px", textAlign: "center" }}>
            <button onClick={loadMore} style={styles.btn}>
              {t("audit.loadMore")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
